import { serviceJsonLd, SHARED_STEPS, SHARED_STATS } from "../types";
import type { LandingContent } from "../types";

const faqs = [
  {
    q: "Do you work on-site in Houston?",
    a: "Yes. We're based in the Greater Houston area and run on-site discovery workshops across Houston, The Woodlands, Sugar Land, Katy and surrounding cities. Remote delivery is also available for Texas businesses outside the metro area — you always work in your own timezone, with a team that answers when you're working.",
  },
  {
    q: "How much does an AI automation project cost?",
    a: "Most first projects for small and mid-size businesses land between $8,000 and $25,000 depending on scope and integrations, with optional monthly optimization after launch. Every engagement starts with a free AI readiness assessment, so you see the projected ROI before spending a dollar.",
  },
  {
    q: "How long does it take to deploy?",
    a: "From audit to automation in about 8 weeks: 1 week for the ROI diagnostic, 2 weeks for design and roadmap, and 4–6 weeks for build and training on your data. Simpler automations can go live sooner.",
  },
  {
    q: "Do you work with small businesses, or only enterprises?",
    a: "Small and mid-size businesses are our core clients — typically 15+ employees. Our engagements are scoped for SMB budgets and timelines, without enterprise consulting overhead.",
  },
  {
    q: "What tools do you integrate with?",
    a: "HubSpot, Salesforce, Slack, Google Workspace, Microsoft 365, Shopify, Zendesk, Intercom, Notion and most modern CRMs, helpdesks and databases via API, Zapier, Make or n8n — plus custom integrations when needed.",
  },
  {
    q: "What happens after launch? Do you disappear?",
    a: "No. Every deployment includes a continuous optimization phase with monthly impact reports. We monitor performance, retrain agents on new data and expand to new use cases as your business grows.",
  },
];

export const houstonContent: LandingContent = {
  seo: {
    title: "AI Automation Agency in Houston, TX | AIdeal Media",
    description:
      "Houston-based AI automation agency. We design, train and deploy custom AI agents and workflows for Texas SMBs — ROI guaranteed in 90 days. Free assessment.",
    keywords: "ai automation agency houston, ai agency houston, ai agents houston, ai automation texas",
    canonical: "https://www.aidealmedia.info/ai-automation-agency-houston/",
    ogImage: "https://www.aidealmedia.info/img/hero-bg.jpg",
    jsonLd: serviceJsonLd({
      url: "https://www.aidealmedia.info/ai-automation-agency-houston/",
      name: "AI Automation Agency in Houston, TX",
      description:
        "Houston-based AI automation agency serving Texas SMBs: custom AI agents, workflow automation and AI roadmaps with ROI guaranteed in 90 days.",
      faqs,
    }),
  },
  badge: "Houston, Texas · Serving SMBs across the Greater Houston area",
  h1a: "AI Automation Agency in ",
  h1gradient: "Houston, TX",
  h1b: "",
  sub: "We help Houston businesses eliminate repetitive work with custom AI agents and automation — designed, deployed and supported in your timezone, with ROI guaranteed in 90 days.",
  problemTitle: "Houston runs on hustle. Your team shouldn't drown in busywork.",
  problemIntro:
    "Across the Greater Houston area, growing businesses lose thousands of hours every year to work that software should already be doing. Sound familiar?",
  pains: [
    {
      icon: "ri-time-line",
      title: "Staff buried in repetitive admin",
      text: "Copying data between systems, chasing paperwork, updating spreadsheets — hours your team could spend serving customers and closing deals.",
    },
    {
      icon: "ri-phone-line",
      title: "Missed calls, slow follow-up",
      text: "Leads that call after hours or wait too long for a reply don't wait — they call your competitor. Speed wins business in Texas.",
    },
    {
      icon: "ri-database-2-line",
      title: "Data scattered everywhere",
      text: "CRM here, inbox there, spreadsheets somewhere else. Nobody has the full picture, so decisions run on gut feeling instead of data.",
    },
  ],
  servicesTitle: "AI automation services in Houston",
  servicesIntro:
    "Every solution is deployed in 4–8 weeks, integrates with your current tools, and runs on transparent, contract-backed metrics.",
  services: [
    {
      icon: "ri-customer-service-2-line",
      title: "AI Sales Agents",
      text: "Automate your funnel — from lead qualification to booked meetings on autopilot, 24/7 across voice and text.",
    },
    {
      icon: "ri-headphone-line",
      title: "AI Support Agents",
      text: "Resolve the majority of tickets without human intervention, with intelligent escalation when it matters.",
    },
    {
      icon: "ri-flow-chart",
      title: "Workflow Automation",
      text: "Connect your CRM, inbox, accounting and ops tools so data flows and busywork disappears.",
    },
    {
      icon: "ri-megaphone-line",
      title: "Marketing Automation",
      text: "End-to-end campaign execution: AI-driven creative, targeting and optimization.",
    },
    {
      icon: "ri-bar-chart-line",
      title: "Data & Business Intelligence",
      text: "Unified dashboards and AI forecasting with actionable alerts — 100% visibility into your business.",
    },
    {
      icon: "ri-road-map-line",
      title: "Strategic AI Roadmap",
      text: "We audit your workflows, pinpoint high-leverage use cases and design an execution plan centered on measurable ROI.",
    },
  ],
  bandTitle: "A local partner, not a distant vendor",
  bandText:
    "Hiring an AI automation agency shouldn't mean timezone roulette and faceless tickets. When you work with AIdeal Media, you get:",
  bandPoints: [
    "Same-timezone team — we answer during your business hours",
    "On-site discovery workshops anywhere in Greater Houston",
    "A bilingual team (English / Spanish) that speaks your customers' language",
    "Direct access to the strategists building your systems — no account-manager telephone game",
  ],
  processTitle: "From audit to automation in 8 weeks",
  processIntro: "A transparent, results-driven process built on measurable KPIs and performance-backed contracts.",
  steps: SHARED_STEPS,
  stats: SHARED_STATS,
  faqTitle: "Frequently asked questions",
  faqs,
  relatedTitle: "Explore more",
  related: [
    {
      href: "/ai-readiness-assessment/",
      label: "Free AI Readiness Assessment",
      desc: "Discover your top 3 ROI opportunities in 30 minutes.",
    },
    {
      href: "/industries/ai-agents-for-law-firms/",
      label: "AI Agents for Law Firms",
      desc: "24/7 intake, triage and automation for firms.",
    },
    {
      href: "/industries/ai-automation-for-logistics/",
      label: "AI Automation for Logistics",
      desc: "Cut back-office costs for 3PLs and distributors.",
    },
  ],
  ctaTitle: "Let's talk about your Houston business",
  ctaText:
    "Spend 30 minutes with an AI strategist and leave with a clear, custom action plan — plus your 90-day ROI estimate. No fluff.",
};
